import { useEffect, useRef, useState } from 'react';
import { formatUsd } from '../utils/networkEditor';

// A step missing from here is shown under its raw name, never dropped.
const STEP_LABEL = {
  tazdata: 'Map zones to roads',
  od2trips: 'Build trip file',
  netconvert: 'Rebuild network',
  zone_weights: 'Price the zone',
  duarouter: 'Assign traffic',
  read_net: 'Reload network',
  metrics: 'Compute metrics',
  duarouter_microsim: 'Microsim demand',
  microsim_before: 'Microsim — baseline',
  microsim_after: 'Microsim — edited',
  microsim_safety: 'Microsim — conflicts',
  ssm_aggregate: 'Score conflicts',
};

/** One line above a Run button, in four states: nothing staged, an estimate, a live
 *  stopwatch against that estimate, and the measured cost. Used by BOTH run buttons.
 *
 *  `phase` is 'macro' | 'safety' | null, not a boolean: a run is two pieces of work
 *  (/run answers in ~4 s, the conflict microsim continues ~16-27 s) and the published
 *  estimate is end-to-end machine time, so the bar spans both. The stopwatch does not
 *  restart at the phase change — `startedAt` is cleared only when `phase` goes null.
 *
 *  `cost` is the SUM of both halves; `costSoFar` is the macro block alone, which is
 *  what the "… so far" caption claims. The sum falls back to macro-only when there is
 *  no job, when it was skipped, and when it failed. */
export default function CostRow({
  est, phase, chainNote, cost, costSoFar, hasWork, idleText, onHelp,
}) {
  const [open, setOpen] = useState(false);
  const [, forceTick] = useState(0);
  const startedAt = useRef(null);
  const running = !!phase;

  // A stopwatch, not real progress: neither half reports anything until it is done, so
  // the estimate is the only scale available.
  useEffect(() => {
    if (!running) { startedAt.current = null; return undefined; }
    // Set once per run, not once per phase -- see the note above.
    if (startedAt.current == null) startedAt.current = Date.now();
    const id = setInterval(() => forceTick((n) => n + 1), 100);
    return () => clearInterval(id);
  }, [running]);

  if (running) {
    const elapsed = startedAt.current ? (Date.now() - startedAt.current) / 1000 : 0;
    const frac = est?.seconds ? Math.min(1, elapsed / est.seconds) : 0;
    // Macro label is the caller's (the two paths run different chains); the safety label
    // is this component's.
    const note = phase === 'safety'
      ? 'Analysing conflicts — a second, finer simulation.'
      : chainNote;
    return (
      <div className="ne-cost">
        <div className="ne-cost-line">
          <span className="ne-cost-elapsed">{elapsed.toFixed(1)}s</span>
          {est && <span className="ne-cost-est">/ ~{est.seconds}s</span>}
          {est?.usd != null && <span className="ne-cost-est">· ~{formatUsd(est.usd)}</span>}
        </div>
        <div className="ne-cost-bar"><span style={{ width: `${frac * 100}%` }} /></div>
        {note && <div className="ne-run-note">{note}</div>}
        {/* Shown from the moment the macro half is done — earlier than the background job. */}
        {phase === 'safety' && costSoFar && (
          <div className="ne-run-note">
            Traffic results are final — {costSoFar.elapsed_s}s ·{' '}
            {formatUsd(costSoFar.usd)} so far.
          </div>
        )}
      </div>
    );
  }

  if (cost) {
    const steps = Object.entries(cost.steps || {})
      .filter(([, sec]) => sec > 0)
      .sort((a, b) => b[1] - a[1]);
    return (
      <div className="ne-cost ne-cost-done">
        <div className="ne-cost-headline">
          <span className="ne-cost-seconds">{cost.elapsed_s}s</span>
          <span className="ne-cost-usd">{formatUsd(cost.usd)}</span>
        </div>
        <div className="ne-cost-sub">
          {cost.rate.instance} @ ${cost.rate.usd_per_hour}/hr
        </div>
        <div className="ne-cost-actions">
          <button className="ne-link" onClick={() => setOpen(!open)}>
            {open ? '▾' : '▸'} Breakdown
          </button>
          <button className="ne-link" onClick={onHelp}>How this is calculated ›</button>
        </div>
        {open && (
          <div className="ne-cost-steps">
            {steps.map(([name, sec]) => (
              <div key={name} className="ne-cost-step">
                <span>{STEP_LABEL[name] || name}</span><span>{sec.toFixed(1)}s</span>
              </div>
            ))}
            {cost.cached?.steps?.length > 0 && (
              <div className="ne-cost-step cached">
                <span>Baseline reused</span>
                <span>saved {cost.cached.saved_s}s ({formatUsd(cost.cached.saved_usd)})</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!hasWork) {
    return <div className="ne-cost ne-cost-idle">{idleText}</div>;
  }

  return (
    <div className="ne-cost">
      <div className="ne-cost-line">
        {est
          ? <><span className="ne-cost-est-lead">Est. ~{est.seconds}s</span>
              <span className="ne-cost-est">· ~{formatUsd(est.usd)}</span></>
          : <span className="ne-cost-est">Estimate unavailable</span>}
        <button className="ne-link ne-cost-why" onClick={onHelp}>How this is calculated ›</button>
      </div>
    </div>
  );
}
